// @flow

import React from "react"
import { observer } from "mobx-react"
import { Form } from "antd"
import styled from "styled-components"
import get from "lodash/get"
import moment from "moment"
import SelectFormItem from "@cabonline/shared/components/form-items/SelectFormItem"
import TextareaFormItem from "@cabonline/shared/components/form-items/TextareaFormItem"
import StringFormItem from "@cabonline/shared/components/form-items/StringFormItem"
import CheckboxGroupFormItem from "@cabonline/shared/components/form-items/CheckboxGroupFormItem"
import VoucherStore from "@cabonline/shared/stores/VoucherStore"
import { mergeFormatMessage } from "@cabonline/shared/components/vouchers/utils/voucherUtils"
import PhoneNumberFormItem from "@cabonline/shared/components/PhoneInput/PhoneNumberFormItem"
import { ANONYMOUS_PHONE_NUMBER } from "@cabonline/shared/utils/constants"
import CustomAmountsButtons from "@cabonline/shared/components/vouchers/CustomAmountsButtons"

type Props = {
  form: Object,
  onFormSubmit: Function,
  formatMessage: Function,
  environment: "admin" | "bc",
  orderDetails: Object,
  traveler?: {
    id?: string,
    phoneNumber?: string
  }
}

const VoucherOrderDetailsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`

const VoucherOrderDetails = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const Error = styled.p`
  color: red;
`

const Details = styled.div`
  width: 100%;
  background-color: #f4f4f4;
  padding: 10px;
  margin: 7px 0 20px;
  display: flex;
  ${p => p.$spaceBetween && "justify-content: space-between;"};
`

const PhoneWrapper = styled.div`
  width: 50%;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${props => {
    if (props.right) return "flex-end"
    if (props.spread) return "space-between"
    return "unset"
  }};

  p {
    margin-bottom: 0px;
    margin-left: 4px;
    padding: 0px;
  }
`

class VoucherForm extends React.Component<Props> {
  componentDidMount() {
    this.getBucketsAndReasonCodes()
  }

  async getBucketsAndReasonCodes() {
    await VoucherStore.getAllBuckets()
  }

  onSelectBucket = (bucketId: string) => {
    const bucket = VoucherStore.allBuckets.find(
      bucket => bucket.id === Number(bucketId)
    )

    VoucherStore.setSelectedBucket(bucket)
  }

  renderVoucherValidDate = () => {
    const { formatMessage, environment } = this.props
    const { selectedBucket } = VoucherStore
    const validUntil = get(
      selectedBucket,
      "voucherSpecification.validUntil",
      ""
    )
    const validityPeriod = get(
      selectedBucket,
      "voucherSpecification.validityPeriod",
      0
    )
    return validUntil ? (
      <span>
        {mergeFormatMessage(
          formatMessage,
          "apps.vouchers.bucket.voucherSettings.validUntil",
          environment
        )}{" "}
        {moment(validUntil).format("YYYY-MM-DD")}
      </span>
    ) : (
      <span>
        {mergeFormatMessage(
          formatMessage,
          "apps.users.travelers.traveler.vouchers.value.validityPeriod",
          environment,
          { date: validityPeriod }
        )}
      </span>
    )
  }

  renderFixedValue = () => {
    const { formatMessage, environment } = this.props
    const { selectedBucket } = VoucherStore
    const voucherValue = get(
      selectedBucket,
      "voucherSpecification.voucherValue",
      0
    )
    if (!voucherValue) return null
    return (
      <div>
        <strong>
          {mergeFormatMessage(
            formatMessage,
            "apps.api-key.property.value",
            environment
          )}
        </strong>
        <Details $spaceBetween>
          <div>
            {selectedBucket.voucherSpecification.voucherValue}{" "}
            {selectedBucket.currency}
          </div>
          <div>{this.renderVoucherValidDate()}</div>
        </Details>
      </div>
    )
  }

  renderVoucherValueField = () => {
    const { getFieldDecorator, setFieldsValue } = this.props.form
    const { formatMessage, environment } = this.props
    const { selectedBucket } = VoucherStore
    if (!selectedBucket) return null
    if (selectedBucket.voucherSpecification.voucherFixedValue) {
      return this.renderFixedValue()
    } else {
      return (
        <CustomAmountsButtons
          mergeFormatMessage={mergeFormatMessage}
          formatMessage={formatMessage}
          getFieldDecorator={getFieldDecorator}
          selectedBucket={selectedBucket}
          setFieldsValue={setFieldsValue}
          renderVoucherValidDate={this.renderVoucherValidDate}
          environment={environment}
        />
      )
    }
  }

  renderReasonCodes = () => {
    const { getFieldDecorator } = this.props.form
    const { formatMessage, environment } = this.props
    const { selectedBucket } = VoucherStore
    const { allReasonCodes } = VoucherStore
    if (!selectedBucket) return

    return (
      <>
        <CheckboxGroupFormItem
          requiredMessage={mergeFormatMessage(
            formatMessage,
            "common.required",
            environment
          )}
          getFieldDecorator={getFieldDecorator}
          name={"reasonCodeIds"}
          options={allReasonCodes}
          label={mergeFormatMessage(
            formatMessage,
            "apps.users.travelers.traveler.vouchers.giveVoucher.compensationLabel",
            environment
          )}
          required
        />
        <TextareaFormItem
          name="information"
          getFieldDecorator={getFieldDecorator}
          label={mergeFormatMessage(
            formatMessage,
            "apps.users.travelers.traveler.vouchers.giveVoucher.informationLabel",
            environment
          )}
        />
        <StringFormItem
          name="roxxId"
          getFieldDecorator={getFieldDecorator}
          label={mergeFormatMessage(
            formatMessage,
            "apps.users.travelers.traveler.vouchers.roxxId",
            environment
          )}
          placeholder={mergeFormatMessage(
            formatMessage,
            "apps.users.travelers.traveler.vouchers.enterId",
            environment
          )}
        />
      </>
    )
  }

  renderBaseDetails() {
    const {
      orderDetails = null,
      traveler,
      formatMessage,
      environment,
      form: { getFieldDecorator }
    } = this.props

    const fullName = get(orderDetails, "traveler.fullName")
    const orderBrandName = get(orderDetails, "order.brand.name")
    const publicId = get(orderDetails, "order.publicId")

    const phoneNumber = get(traveler, "phoneNumber")
    const phoneNumberToFill =
      phoneNumber === ANONYMOUS_PHONE_NUMBER ? "" : phoneNumber

    return (
      <VoucherOrderDetailsWrapper>
        <VoucherOrderDetails>
          {orderDetails && (
            <div>
              <Row spread>
                <p>
                  <strong>{fullName}</strong>
                </p>
                <p>
                  <strong>{orderBrandName}</strong>
                </p>
              </Row>
              <Row right>
                <p>
                  {mergeFormatMessage(
                    formatMessage,
                    "orderBox.orderId",
                    environment
                  )}{" "}
                  <span>{publicId}</span>
                </p>
              </Row>
            </div>
          )}
          <PhoneWrapper>
            <PhoneNumberFormItem
              getFieldDecorator={getFieldDecorator}
              required
              initialValue={phoneNumberToFill}
              label={mergeFormatMessage(
                formatMessage,
                "apps.users.travelers.traveler.vouchers.giveVoucher.phone",
                environment
              )}
            />
          </PhoneWrapper>
        </VoucherOrderDetails>
      </VoucherOrderDetailsWrapper>
    )
  }

  renderSelect() {
    const { getFieldDecorator } = this.props.form
    const { formatMessage, environment } = this.props
    const { allBuckets, loading } = VoucherStore

    const normalizedBuckets = allBuckets.map(bucket => ({
      key: bucket.id,
      name: bucket.name
    }))

    const placeholder = loading
      ? mergeFormatMessage(formatMessage, "common.loading", environment)
      : mergeFormatMessage(
          formatMessage,
          "apps.users.travelers.traveler.vouchers.giveVoucher.voucherLabel",
          environment
        )

    if (normalizedBuckets.length === 0) {
      return (
        <Error>
          {mergeFormatMessage(
            formatMessage,
            "apps.users.travelers.traveler.vouchers.giveVoucher.noBuckets",
            environment
          )}
        </Error>
      )
    }

    return (
      <SelectFormItem
        getFieldDecorator={getFieldDecorator}
        label={mergeFormatMessage(
          formatMessage,
          "apps.users.travelers.traveler.vouchers.giveVoucher.voucherLabel",
          environment
        )}
        name="buckets"
        mode="default"
        options={normalizedBuckets}
        loading={loading}
        onChange={this.onSelectBucket}
        required
        disabled={loading}
        placeholder={placeholder}
      />
    )
  }

  render() {
    const { onFormSubmit } = this.props

    return (
      <Form onSubmit={values => onFormSubmit(values)}>
        {this.renderBaseDetails()}
        {this.renderSelect()}
        {this.renderVoucherValueField()}
        {this.renderReasonCodes()}
      </Form>
    )
  }
}

export default Form.create()(observer(VoucherForm))
